import React, { useState, useEffect, useRef, useContext } from "react";
import { Container } from "components";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "components/molecules/Breadcrumb";
import { CKEditor } from "ckeditor4-react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { postData, putData } from "actions";
import { API_URL_banList } from "constants/api";
import { banListReducers } from "redux/banListSlice";
import { AuthContext } from "context/AuthContext";

const FormBanlist = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const formik = useFormik({
    initialValues: {
      email_address: location.state ? location.state.item.email_address : "",
      ban_status: location.state ? location.state.item.ban_status : "",
      internal_notes: location.state ? location.state.item.internal_notes : "",
    },
    validationSchema: Yup.object({
      email_address: Yup.string()
        .email("Email Address must type E-mail")
        .required("Email Address is required"),
      ban_status: Yup.string().required("Ban Status is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_banList,
          formData,
          { dispatch, redux: banListReducers },
          "ADD_BANLIST",
          () => {
            navigate("/administrator/email/banlists");
          }
        );
      } else {
        putData(
          `${API_URL_banList}${location.state.item.id}/`,
          formData,
          { dispatch, redux: banListReducers },
          "ADD_BANLIST",
          () => {
            navigate("/administrator/email/banlists");
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Ban email"}
        excludedPaths={["email", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          Create a banned email
        </div>
        <form onSubmit={handleFormSubmit}>
          <div className="h-auto w-auto px-4 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-3">
              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Email address <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="email"
                    name="email_address"
                    id="email"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.email_address}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.email_address &&
                    formik.errors.email_address && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.email_address}
                      </div>
                    )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Ban Status <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap">
                  <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                    <input
                      id="ban_status"
                      type="radio"
                      name="ban_status"
                      checked={formik.values.ban_status === 1}
                      onChange={() => formik.setFieldValue("ban_status", 1)}
                      ref={(ref) => (inputRefs.current[1] = ref)}
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <span className="ml-2 mr-5">Active</span>
                  </label>
                  <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                    <input
                      id="ban_status"
                      type="radio"
                      name="ban_status"
                      checked={formik.values.ban_status === 0}
                      onChange={() => formik.setFieldValue("ban_status", 0)}
                      ref={(ref) => (inputRefs.current[2] = ref)}
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <span className="ml-2">Inactive</span>
                  </label>
                </div>
                {formik.touched.ban_status && formik.errors.ban_status && (
                  <div className="text-red-500 dark:text-red-300 text-xs">
                    {formik.errors.ban_status}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 mb-5">
              <label className="text-xs mr-1 md:-text-sm lg:text-sm mb-1">
                Internal notes
              </label>
              <div>
                <CKEditor
                  config={toolbarConfig}
                  type="classic"
                  initData={formik.values.internal_notes}
                  onChange={(e) => {
                    formik.setFieldValue("internal_notes", e.editor.getData());
                  }}
                />
              </div>
            </div>
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default FormBanlist;
